import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

type TurnstileRenderOptions = {
    callback(token: string): void;
    'error-callback'(): void;
    'expired-callback'(): void;
    language: string;
    sitekey: string;
    size: 'flexible';
};

type Turnstile = {
    remove(widgetId: string): void;
    render(container: HTMLElement, options: TurnstileRenderOptions): string;
    reset(widgetId: string): void;
};

declare global {
    interface Window {
        turnstile?: Turnstile;
    }
}

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let loadingTurnstile: Promise<Turnstile> | undefined;

/**
 * Le script n'est chargé qu'une fois, et seulement sur l'écran qui en a besoin. Un échec n'est pas
 * mémorisé : revenir sur l'écran retente le chargement.
 */
const loadTurnstile = (): Promise<Turnstile> => {
    if (!loadingTurnstile) {
        loadingTurnstile = new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = SCRIPT_URL;
            script.async = true;
            script.onload = () =>
                window.turnstile ? resolve(window.turnstile) : reject(new Error('Turnstile missing'));
            script.onerror = () => {
                loadingTurnstile = undefined;
                script.remove();
                reject(new Error('Turnstile could not be loaded'));
            };
            document.head.appendChild(script);
        });
    }

    return loadingTurnstile;
};

export type CaptchaHandle = {
    /** Un jeton ne sert qu'une fois : à rappeler après chaque envoi au serveur. */
    reset(): void;
};

type CaptchaProps = {
    siteKey: string;
    /** Le jeton du défi réussi, ou null quand il n'y en a plus de valable. */
    onTokenChange(token: string | null): void;
    /** Le widget n'a pas pu se charger ou délivrer de jeton : bloqueur, réseau, panne de Cloudflare. */
    onError(): void;
};

const Captcha = forwardRef<CaptchaHandle, CaptchaProps>(({ siteKey, onTokenChange, onError }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const widgetIdRef = useRef<string>();
    // Les callbacks changent à chaque rendu du parent : le widget, lui, n'est créé qu'une fois.
    const callbacksRef = useRef({ onError, onTokenChange });
    callbacksRef.current = { onError, onTokenChange };

    useImperativeHandle(ref, () => ({
        reset: () => {
            callbacksRef.current.onTokenChange(null);
            if (window.turnstile && widgetIdRef.current) {
                window.turnstile.reset(widgetIdRef.current);
            }
        },
    }));

    useEffect(() => {
        let cancelled = false;

        loadTurnstile()
            .then((turnstile) => {
                if (cancelled || !containerRef.current) {
                    return;
                }
                widgetIdRef.current = turnstile.render(containerRef.current, {
                    callback: (token) => callbacksRef.current.onTokenChange(token),
                    'error-callback': () => {
                        callbacksRef.current.onTokenChange(null);
                        callbacksRef.current.onError();
                    },
                    'expired-callback': () => callbacksRef.current.onTokenChange(null),
                    language: 'fr',
                    sitekey: siteKey,
                    size: 'flexible',
                });
            })
            .catch(() => {
                if (!cancelled) {
                    callbacksRef.current.onError();
                }
            });

        return () => {
            cancelled = true;
            if (window.turnstile && widgetIdRef.current) {
                window.turnstile.remove(widgetIdRef.current);
                widgetIdRef.current = undefined;
            }
        };
    }, [siteKey]);

    return <div ref={containerRef} />;
});

Captcha.displayName = 'Captcha';

export default Captcha;
